import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { ArrowRight, TrendingUp, Users, Building2, Shield, ChevronRight, Menu, X, Sparkles, Star, XCircle, AlertTriangle, FileX, BarChart3, Lock, UserX, Search, Network, UserCheck, FileCheck, CheckCircle2, Zap } from "lucide-react";
import AnimatedCounter from "../components/AnimatedCounter";
import FloatingActionButton from "../components/FloatingActionButton";
import ThemeToggle from "../components/ThemeToggle"; // keeping import for other pages
import AnimatedLogo from "../components/AnimatedLogo";
import CampusCarousel from "../components/CampusCarousel";
import { motion } from "framer-motion";

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `${(value / 100000).toFixed(2)} L`;
  }
  return value.toLocaleString('en-IN');
};

const formatCurrencyShort = (value) => {
  if (value >= 10000000) {
    return (value / 10000000).toFixed(1);
  } else if (value >= 100000) {
    return (value / 100000).toFixed(1);
  }
  return value;
};

const Landing = () => {
  const { user, login, sessionLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_placements: 1499,
    total_colleges: 99,
    total_programs: 13,
    avg_ctc: 2433591
  }); // Default fallback values
  const [topRankings, setTopRankings] = useState([]);
  const [latestPlacements, setLatestPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // iOS-style glass effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cache for rankings data
  const rankingsCache = useRef(null);
  const retryTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    const fetchData = async (isRetry = false) => {
      try {
        // Use separate try-catch for each request to ensure partial failures don't break everything
        const statsPromise = axios.get(`${API}/stats`, { 
          timeout: 15000,
          withCredentials: false,
          signal: abortControllerRef.current?.signal
        }).catch(e => {
          if (e.name !== 'AbortError' && !axios.isCancel(e)) {
            console.error("Stats fetch error:", e);
          }
          return null;
        });
        
        const rankingsPromise = axios.get(`${API}/rankings?limit=5`, { 
          timeout: 15000,
          withCredentials: false,
          signal: abortControllerRef.current?.signal
        }).catch(e => {
          if (e.name !== 'AbortError' && !axios.isCancel(e)) {
            console.error("Rankings fetch error:", e);
          }
          return null;
        });
        
        const latestPromise = axios.get(`${API}/latest-placements?limit=10`, { 
          timeout: 15000,
          withCredentials: false,
          signal: abortControllerRef.current?.signal
        }).catch(e => {
          if (e.name !== 'AbortError' && !axios.isCancel(e)) {
            console.error("Latest placements fetch error:", e);
          }
          return null;
        });

        const [statsRes, rankingsRes, latestRes] = await Promise.all([
          statsPromise,
          rankingsPromise,
          latestPromise
        ]);
        
        if (!isMounted) return;
        
        if (statsRes?.data) {
          setStats(statsRes.data);
        }
        if (rankingsRes?.data && Array.isArray(rankingsRes.data) && rankingsRes.data.length > 0) {
          setTopRankings(rankingsRes.data);
          rankingsCache.current = rankingsRes.data; // Cache successful data
          retryCount = 0; // Reset retry count on success
        } else if (rankingsCache.current && rankingsCache.current.length > 0) {
          // Use cached data if API returned empty - DON'T clear existing data
          console.log("Using cached rankings on Landing");
          setTopRankings(rankingsCache.current);
        } else if (topRankings.length > 0) {
          // Keep showing existing data if we have any
          console.log("Keeping existing rankings on Landing");
        } else if (retryCount < maxRetries && !isRetry) {
          // Retry if no data and no cache
          retryCount++;
          const delay = Math.pow(2, retryCount) * 1000;
          console.log(`Rankings empty, retrying in ${delay/1000}s`);
          retryTimeoutRef.current = setTimeout(() => fetchData(true), delay);
          return;
        }
        if (latestRes?.data) {
          setLatestPlacements(latestRes.data);
        }
      } catch (error) {
        // Ignore aborted requests
        if (axios.isCancel(error) || error.name === 'AbortError') {
          return;
        }
        console.error("Error fetching data:", error);
        // Use cached rankings if available - DON'T clear existing data
        if (rankingsCache.current && isMounted) {
          setTopRankings(rankingsCache.current);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    // Use cached data immediately if available
    if (rankingsCache.current && rankingsCache.current.length > 0) {
      setTopRankings(rankingsCache.current);
      setLoading(false);
    }
    
    fetchData();
    
    return () => {
      isMounted = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Close mobile menu on navigation
  const handleMobileNav = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation - Glassmorphism */}
      <nav className="sticky top-0 z-50 px-4 pt-3">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between h-12 px-5 rounded-full bg-background/60 backdrop-blur-xl border border-border/50 shadow-sm">
            <AnimatedLogo />
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              <Link 
                to="/dashboard" 
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-secondary/50" 
                data-testid="nav-rankings"
              >
                Rankings
              </Link>
              <Link 
                to="/about" 
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-secondary/50"
              >
                About
              </Link>
              <div className="w-px h-5 bg-border mx-1" />
              {user ? (
                <>
                  <Link to="/submit" className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-secondary/50" data-testid="nav-submit">
                    Submit
                  </Link>
                  <Link to="/profile" data-testid="nav-profile">
                    <Button variant="ghost" size="sm" className="h-9 px-4 text-sm font-medium rounded-full">
                      Profile
                    </Button>
                  </Link>
                </>
              ) : sessionLoading ? (
                <Button disabled className="h-9 px-5 text-sm font-medium rounded-full shadow-sm ml-2" data-testid="nav-login-btn">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </Button>
              ) : (
                <Button 
                  onClick={() => login('/submit')} 
                  className="h-9 px-6 text-sm font-medium rounded-full shadow-sm ml-3 bg-gray-700 hover:bg-gray-800 text-white" 
                  data-testid="nav-login-btn"
                >
                  Sign in
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 md:hidden">
              <button 
                className="p-2 rounded-full hover:bg-secondary/50 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <motion.div 
          className="fixed inset-0 bg-white dark:bg-slate-950 z-40 md:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="flex flex-col p-6 pt-24 gap-2">
            <motion.button 
              onClick={() => handleMobileNav('/dashboard')}
              className="py-4 px-4 text-lg font-medium rounded-xl hover:bg-secondary/50 text-left transition-colors"
              data-testid="mobile-nav-rankings"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              Rankings
            </motion.button>
            <motion.button 
              onClick={() => handleMobileNav('/about')}
              className="py-4 px-4 text-lg font-medium rounded-xl hover:bg-secondary/50 text-left transition-colors"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              About Us
            </motion.button>
            {user ? (
              <>
                <motion.button 
                  onClick={() => handleMobileNav('/submit')}
                  className="py-4 px-4 text-lg font-medium rounded-xl hover:bg-secondary/50 text-left transition-colors"
                  data-testid="mobile-nav-submit"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  Submit Placement
                </motion.button>
                <motion.button 
                  onClick={() => handleMobileNav('/profile')}
                  className="py-4 px-4 text-lg font-medium rounded-xl hover:bg-secondary/50 text-left transition-colors"
                  data-testid="mobile-nav-profile"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 }}
                >
                  My Profile
                </motion.button>
              </>
            ) : sessionLoading ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-4"
              >
                <Button 
                  disabled
                  className="w-full h-12 font-medium text-sm rounded-2xl shadow-lg"
                  data-testid="mobile-nav-login"
                >
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </Button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-4"
              >
                <Button 
                  onClick={() => { setMobileMenuOpen(false); login('/submit'); }}
                  className="w-full h-12 font-medium text-sm rounded-2xl shadow-lg"
                  data-testid="mobile-nav-login"
                >
                  Sign in with Google
                </Button>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-background to-background pointer-events-none" />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.015)_1px,transparent_1px)] bg-[size:72px_72px] pointer-events-none" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.div 
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-secondary/80 border border-border/50 rounded-full text-xs font-medium text-muted-foreground mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                Trusted by Graduates of Top Institutes
              </motion.div>
              
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.05]">
                <motion.span
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="block"
                >
                  No More Fake
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.6 }}
                  className="block text-muted-foreground/70"
                >
                  Placement Stats
                </motion.span>
              </h1>
              
              <motion.p 
                className="mt-6 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                Real salaries reported by real students. No inflated numbers, no marketing spin. 
                Just verified placement data to help you make informed decisions.
              </motion.p>
              
              <motion.div 
                className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75, duration: 0.6 }}
              >
                <Button 
                  onClick={() => navigate('/dashboard')} 
                  size="lg"
                  className="h-12 px-6 text-sm font-medium rounded-2xl shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 transition-all group"
                  data-testid="hero-view-rankings-btn"
                >
                  View Rankings
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => user ? navigate('/submit') : login('/submit')}
                  className="h-12 px-6 text-sm font-medium rounded-2xl border-border/50 hover:bg-secondary/50 transition-all"
                  data-testid="hero-submit-btn"
                >
                  Submit Your Placement
                </Button>
              </motion.div>
              
              {/* Trust indicators */}
              <motion.div 
                className="mt-10 pt-8 border-t border-border/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.6 }}
              >
                <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-emerald-500" />
                    <span>AI Verified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-500" />
                    <span>
                      {stats.total_placements}+ Reports
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-purple-500" />
                    <span>
                      {stats.total_colleges} Colleges
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Desktop Carousel */}
            <motion.div 
              className="lg:col-span-5 hidden lg:block relative"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {/* Floating stat cards */}
              <motion.div 
                className="absolute -top-4 -left-4 bg-background/90 backdrop-blur-sm border border-border/30 p-4 rounded-3xl z-10 shadow-lg"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
                transition={{ 
                  opacity: { delay: 0.8 },
                  scale: { delay: 0.8 },
                  y: { delay: 1.2, duration: 4, repeat: Infinity, ease: "easeInOut" }
                }}
              >
                <div className="text-xs text-muted-foreground font-medium mb-1">Avg Package</div>
                <div className="font-mono font-bold text-xl">
                  {`₹${formatCurrencyShort(stats.avg_ctc)}L`}
                </div>
              </motion.div>
              
              <motion.div 
                className="absolute -bottom-4 -right-4 bg-background/90 backdrop-blur-sm border border-border/30 p-4 rounded-3xl z-10 shadow-lg"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1, y: [0, -10, 0] }}
                transition={{ 
                  opacity: { delay: 1 },
                  scale: { delay: 1 },
                  y: { delay: 1.5, duration: 5, repeat: Infinity, ease: "easeInOut" }
                }}
              >
                <div className="text-xs text-muted-foreground font-medium mb-1">Top Colleges</div>
                <div className="font-mono font-bold text-xl">
                  {`${stats.total_colleges}+`}
                </div>
              </motion.div>
              
              <div className="relative rounded-3xl overflow-hidden border border-border/30 shadow-2xl">
                <CampusCarousel />
              </div>
            </motion.div>
          </div>
          
          {/* Mobile/Tablet Carousel - shown on mobile and tablet (below lg/1024px), hidden on desktop */}
          <motion.div 
            className="mt-8 lg:hidden relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {/* Floating stat cards positioned on diagonal corners OUTSIDE the carousel */}
            <div className="relative max-w-xl mx-auto sm:max-w-2xl md:max-w-3xl px-4">
              <motion.div 
                className="absolute -top-3 -left-1 sm:-top-4 sm:-left-2 glass-card p-2 sm:p-3 rounded-2xl z-10 shadow-lg"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="text-[10px] sm:text-xs text-muted-foreground">Avg Package</div>
                <div className="font-mono font-bold text-sm sm:text-lg">
                  {`₹${formatCurrencyShort(stats.avg_ctc)}L`}
                </div>
              </motion.div>
              <motion.div 
                className="absolute -bottom-3 -right-1 sm:-bottom-4 sm:-right-2 glass-card p-2 sm:p-3 rounded-2xl z-10 shadow-lg"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              >
                <div className="text-[10px] sm:text-xs text-muted-foreground">Top Colleges</div>
                <div className="font-mono font-bold text-sm sm:text-lg">
                  {`${stats.total_colleges}+`}
                </div>
              </motion.div>
              
              <div className="rounded-3xl overflow-hidden">
                <CampusCarousel />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Latest Placements Ticker */}
      {latestPlacements.length > 0 && (
        <section className="border-b border-border py-4 overflow-hidden bg-primary text-primary-foreground relative">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent shimmer pointer-events-none" />
          <div className="flex animate-ticker whitespace-nowrap">
            {[...latestPlacements, ...latestPlacements].map((p, i) => (
              <div key={i} className="flex items-center">
                <span className="font-medium">{p.role}</span>
                <span className="text-primary-foreground/70 mx-1">@</span>
                <span className="font-medium">{p.company}</span>
                <span className="font-mono font-bold text-primary-foreground/90 ml-2">{formatCurrency(p.total_ctc)}</span>
                <span className="text-primary-foreground/40 mx-3">•</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Top Rankings Preview */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight">
                Top Ranked Colleges
              </h2>
              <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">By average placement CTC</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ x: 5 }}
            >
              <Link 
                to="/dashboard" 
                className="text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all hover:text-foreground/70 group"
                data-testid="view-all-rankings-link"
              >
                View all rankings
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block rounded-2xl overflow-hidden border border-border/40 bg-background shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/40 bg-secondary/30">
                  <th className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wider py-4 px-3 w-[70px]">Rank</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider py-4 px-4">College</th>
                  <th className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wider py-4 px-3 w-[90px]">Program</th>
                  <th className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wider py-4 px-3 w-[80px]">Branch</th>
                  <th className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wider py-4 px-3 w-[100px]">Avg CTC</th>
                  <th className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wider py-4 px-3 w-[80px]">Verified</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="hover:bg-secondary/20 transition-colors">
                      <td className="text-center py-5 px-3"><div className="skeleton h-8 w-8 mx-auto rounded-full" /></td>
                      <td className="py-5 px-4"><div className="skeleton h-5 w-32" /></td>
                      <td className="text-center py-5 px-3"><div className="skeleton h-6 w-14 mx-auto rounded-full" /></td>
                      <td className="text-center py-5 px-3"><div className="skeleton h-6 w-10 mx-auto rounded" /></td>
                      <td className="text-center py-5 px-3"><div className="skeleton h-5 w-16 mx-auto" /></td>
                      <td className="text-center py-5 px-3"><div className="skeleton h-7 w-7 mx-auto rounded-full" /></td>
                    </tr>
                  ))
                ) : (
                  topRankings.map((ranking, index) => (
                    <tr 
                      key={`${ranking.college_id}-${ranking.program_id}`} 
                      className="hover:bg-secondary/30 transition-colors cursor-pointer group"
                      onClick={() => navigate(`/college/${ranking.college_id}`)}
                      data-testid={`ranking-row-${index}`}
                    >
                      <td className="text-center py-5 px-3">
                        <div 
                          className="w-9 h-9 mx-auto flex items-center justify-center font-mono text-sm font-bold rounded-full"
                          style={
                            index === 0 
                              ? { background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#1a1a1a' }
                              : index === 1 
                              ? { background: 'linear-gradient(135deg, #E8E8E8 0%, #B8B8B8 100%)', color: '#1a1a1a' }
                              : index === 2 
                              ? { background: 'linear-gradient(135deg, #CD7F32 0%, #A0522D 100%)', color: '#fff' }
                              : { background: 'transparent', color: '#64748b', border: '2px solid #e2e8f0' }
                          }
                        >
                          {ranking.rank}
                        </div>
                      </td>
                      <td className="py-5 px-4">
                        <div className="font-semibold text-sm group-hover:text-primary transition-colors">{ranking.college_name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{ranking.location}</div>
                      </td>
                      <td className="text-center py-5 px-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 text-xs font-medium">
                          {ranking.program_short}
                        </span>
                      </td>
                      <td className="text-center py-5 px-3">
                        <span className="text-sm font-medium text-foreground">
                          {ranking.branch_short || 'CSE'}
                        </span>
                      </td>
                      <td className="text-center py-5 px-3">
                        <span className="font-mono font-bold text-sm text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(ranking.avg_ctc)}
                        </span>
                      </td>
                      <td className="text-center py-5 px-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 font-mono text-xs font-bold">
                          {ranking.placement_count}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <div key={i} className="p-4 rounded-2xl border border-border bg-background">
                  <div className="flex items-center gap-3">
                    <div className="skeleton h-10 w-10 rounded-full" />
                    <div className="flex-1">
                      <div className="skeleton h-4 w-32 mb-2" />
                      <div className="skeleton h-3 w-24" />
                    </div>
                    <div className="skeleton h-6 w-16" />
                  </div>
                </div>
              ))
            ) : (
              topRankings.map((ranking, index) => (
                <motion.div
                  key={`${ranking.college_id}-${ranking.program_id}`}
                  className="p-4 rounded-2xl border border-border bg-background hover:border-foreground/20 transition-colors cursor-pointer active:scale-[0.99]"
                  onClick={() => navigate(`/college/${ranking.college_id}`)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`ranking-card-${index}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Rank Badge */}
                    <div 
                      className="w-10 h-10 flex-shrink-0 flex items-center justify-center font-mono text-sm font-bold rounded-full"
                      style={
                        index === 0 
                          ? { background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#1a1a1a' }
                          : index === 1 
                          ? { background: 'linear-gradient(135deg, #E8E8E8 0%, #B8B8B8 100%)', color: '#1a1a1a' }
                          : index === 2 
                          ? { background: 'linear-gradient(135deg, #CD7F32 0%, #A0522D 100%)', color: '#fff' }
                          : { background: 'transparent', color: '#64748b', border: '2px solid #e2e8f0' }
                      }
                    >
                      {ranking.rank}
                    </div>
                    
                    {/* College Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{ranking.college_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{ranking.location}</div>
                    </div>
                    
                    {/* CTC */}
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono font-bold text-sm text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(ranking.avg_ctc)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">Avg CTC</div>
                    </div>
                  </div>
                  
                  {/* Bottom row with tags */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 text-[10px] font-medium">
                      {ranking.program_short}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary text-[10px] font-medium">
                      {ranking.branch_short || 'CSE'}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      {ranking.placement_count} verified
                    </span>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* The Broken System Section - With Animations */}
      <section className="py-12 sm:py-16 relative overflow-hidden">
        {/* Gradient background for emphasis */}
        <div className="absolute inset-0 bg-gradient-to-b from-rose-500/5 via-background to-background dark:from-rose-500/10" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-1 bg-gradient-to-r from-transparent via-rose-500/40 to-transparent" />
        
        {/* Floating warning particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div 
            className="absolute top-1/4 left-1/4 w-2 h-2 bg-rose-500/30 rounded-full"
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-rose-500/20 rounded-full"
            animate={{ y: [0, -15, 0], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          />
          <motion.div 
            className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-rose-500/25 rounded-full"
            animate={{ y: [0, -25, 0], opacity: [0.25, 0.5, 0.25] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          />
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rose-500/30 bg-rose-500/10 mb-6"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
              >
                <AlertTriangle className="w-4 h-4 text-rose-500" />
              </motion.div>
              <span className="text-rose-500 text-xs font-semibold uppercase tracking-wider">The Broken System</span>
            </motion.div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              Why You <span className="text-rose-500">Can't Trust</span>
              <br />
              Salary Information in India
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              The placement data ecosystem is broken. Here's what's really happening behind the numbers.
            </p>
          </motion.div>
          
          {/* Cards Grid - With staggered animations */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                num: "01",
                icon: Building2,
                title: "College Placement Cells",
                description: "Placement cells are incentivized to inflate CTC figures to attract more admissions."
              },
              {
                num: "02",
                icon: BarChart3,
                title: "Aggregator Platforms",
                description: "Accept paid promotions that boost college visibility. Hard to distinguish organic vs. paid info."
              },
              {
                num: "03",
                icon: FileX,
                title: "CTC Without Breakup",
                description: "Reports show 'CTC' without breakdown. ₹30L with ₹10L ESOPs vs ₹30L cash are very different."
              },
              {
                num: "04",
                icon: AlertTriangle,
                title: "Self-Reported Rankings",
                description: "Rankings based on self-reported data lack verification. Institutions can fabricate information."
              },
              {
                num: "05",
                icon: Lock,
                title: "Limited Standards",
                description: "Reporting standards only cover ~29 B-schools. Not applicable to engineering or other colleges."
              },
              {
                num: "06",
                icon: UserX,
                title: "Anonymous Platforms",
                description: "Show salary ranges but don't verify submissions. Anyone can enter data. Zero accountability."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                {/* Card */}
                <div className="relative p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-background border border-rose-500/20 h-full">
                  {/* Number badge */}
                  <div className="absolute -top-3 left-4 sm:left-6 px-2 sm:px-3 py-0.5 sm:py-1 bg-rose-500 text-white text-xs sm:text-sm font-bold rounded-full">
                    {item.num}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-rose-500/10 flex items-center justify-center mb-3 sm:mb-4 mt-1 sm:mt-2">
                    <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-rose-500" />
                  </div>
                  
                  <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trust Our Data - How We Solve It */}
      <section className="py-12 sm:py-16 relative overflow-hidden">
        {/* Gradient background for emphasis */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 via-background to-background dark:from-emerald-500/10" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-1 bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        
        {/* Floating success particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div 
            className="absolute top-1/4 right-1/4 w-2 h-2 bg-emerald-500/30 rounded-full"
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute top-1/3 left-1/3 w-1.5 h-1.5 bg-emerald-500/20 rounded-full"
            animate={{ y: [0, -15, 0], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          />
          <motion.div 
            className="absolute bottom-1/3 right-1/3 w-2 h-2 bg-emerald-500/25 rounded-full"
            animate={{ y: [0, -25, 0], opacity: [0.25, 0.5, 0.25] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          />
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 mb-6"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </motion.div>
              <span className="text-emerald-600 dark:text-emerald-400 text-xs font-semibold uppercase tracking-wider">Our Solution</span>
            </motion.div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              How We <span className="text-emerald-500">Fix This</span>
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              4-layer verification ensures every data point is authentic and trustworthy.
            </p>
          </motion.div>
          
          {/* Cards Grid - Responsive layout */}
          {/* Desktop: 4 columns, Tablet: 2 columns, Mobile: Single column with horizontal scroll or stacked */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
            {[
              {
                num: "01",
                icon: FileCheck,
                title: "AI Document Analysis",
                description: "ML scans every offer letter for manipulation and forged PDFs.",
                stat: "99.2%",
                label: "Accuracy"
              },
              {
                num: "02",
                icon: BarChart3,
                title: "Market Benchmark",
                description: "Real-time comparison against verified industry salaries.",
                stat: "Live",
                label: "Data"
              },
              {
                num: "03",
                icon: Network,
                title: "Peer Validation",
                description: "Cross-reference with same batch and company submissions.",
                stat: "Smart",
                label: "Detection"
              },
              {
                num: "04",
                icon: UserCheck,
                title: "Human Review",
                description: "Expert team validates edge cases before publication.",
                stat: "100%",
                label: "Verified"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                {/* Card - Mobile: horizontal layout, Desktop: vertical layout */}
                <div className="relative p-4 sm:p-5 lg:p-6 rounded-2xl bg-background border border-emerald-500/20 h-full hover:border-emerald-500/40 transition-colors">
                  {/* Number badge */}
                  <div className="absolute -top-3 left-4 sm:left-5 lg:left-6 px-2.5 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full shadow-lg">
                    {item.num}
                  </div>
                  
                  {/* Mobile: Horizontal layout */}
                  <div className="flex sm:block items-start gap-4 mt-2 sm:mt-0">
                    {/* Icon */}
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0 sm:mb-4 sm:mt-2">
                      <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-500" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2 leading-tight">{item.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-none">
                        {item.description}
                      </p>
                      
                      {/* Stat - Inline on mobile */}
                      <div className="pt-3 border-t border-emerald-500/10">
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl sm:text-2xl font-bold text-emerald-500">{item.stat}</span>
                          <span className="text-xs text-muted-foreground uppercase tracking-wider">{item.label}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Bottom trust indicator */}
          <motion.div 
            className="mt-8 sm:mt-12 flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2 sm:py-3 rounded-full border border-emerald-500/20 bg-emerald-500/5">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
              <span className="text-xs sm:text-sm text-muted-foreground">All 4 layers must pass before publication</span>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Testimonials - Smooth Marquee */}
      <section className="py-12 sm:py-16 relative bg-background overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 sm:mb-12">
          {/* Header */}
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
              What Students Say
            </h2>
            <p className="text-muted-foreground mt-2 sm:mt-3 text-sm sm:text-base">Trusted by students from top institutions</p>
          </motion.div>
        </div>
        
        {/* Horizontal Scrolling Cards - CSS Animation for smoothness */}
        <div className="relative">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-24 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          
          {/* First row - moves left */}
          <div className="marquee-container mb-4 sm:mb-6">
            <div className="marquee-content animate-marquee-left">
              {[...Array(2)].map((_, setIndex) => (
                <div key={setIndex} className="flex gap-4 sm:gap-6">
                  {[
                    { quote: "The only platform where I can trust the placement data. Other sites just aggregate numbers without any verification.", college: "IIT Madras", batch: "'23" },
                    { quote: "Finally, a transparent view of actual salaries. Helped me negotiate a 20% higher package.", college: "IIM Ahmedabad", batch: "'22" },
                    { quote: "No more inflated numbers. This is what placement reports should be - honest and verified.", college: "IIT Bombay", batch: "'23" },
                    { quote: "Game changer for placement preparation. Real insights from real students.", college: "BITS Pilani", batch: "'24" },
                  ].map((testimonial, index) => (
                    <div
                      key={`${setIndex}-${index}`}
                      className="w-[300px] sm:w-[380px] flex-shrink-0 p-5 sm:p-6 rounded-2xl border border-border bg-background hover:border-foreground/20 transition-colors"
                    >
                      <p className="text-sm sm:text-base text-foreground leading-relaxed mb-4">
                        "{testimonial.quote}"
                      </p>
                      <div className="flex items-center gap-3 pt-4 border-t border-border">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-foreground flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-background">
                            {testimonial.college.split(' ').map(w => w[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground">{testimonial.college}</div>
                          <div className="text-xs text-muted-foreground">Batch {testimonial.batch}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          {/* Second row - moves right */}
          <div className="marquee-container">
            <div className="marquee-content animate-marquee-right">
              {[...Array(2)].map((_, setIndex) => (
                <div key={setIndex} className="flex gap-4 sm:gap-6">
                  {[
                    { quote: "Before this, I had no way to verify if colleges were honest about placements. Now I can see the real picture.", college: "IIT Delhi", batch: "'24" },
                    { quote: "The verification process is rigorous. My offer was verified within 48 hours with complete accuracy.", college: "IIM Bangalore", batch: "'23" },
                    { quote: "Real data from real students. This should be the industry standard for all placement reporting.", college: "IIT Kharagpur", batch: "'24" },
                    { quote: "Helped me compare offers across companies. The salary breakdown feature is incredibly useful.", college: "NIT Trichy", batch: "'23" },
                  ].map((testimonial, index) => (
                    <div
                      key={`${setIndex}-${index}`}
                      className="w-[300px] sm:w-[380px] flex-shrink-0 p-5 sm:p-6 rounded-2xl border border-border bg-background hover:border-foreground/20 transition-colors"
                    >
                      <p className="text-sm sm:text-base text-foreground leading-relaxed mb-4">
                        "{testimonial.quote}"
                      </p>
                      <div className="flex items-center gap-3 pt-4 border-t border-border">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-foreground flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-background">
                            {testimonial.college.split(' ').map(w => w[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground">{testimonial.college}</div>
                          <div className="text-xs text-muted-foreground">Batch {testimonial.batch}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Subtle Minimal Monochrome */}
      <section className="py-12 sm:py-16 relative bg-slate-950 overflow-hidden">
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        
        {/* Subtle radial gradient */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-slate-800/30 rounded-full blur-[120px]" />
        
        {/* Top fade line */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
        
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          {/* Heading */}
          <motion.h2 
            className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Help make placements transparent
          </motion.h2>
          
          <motion.p 
            className="text-slate-400 mt-5 max-w-lg mx-auto text-base leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Your anonymous submission helps thousands of students make better decisions. Takes less than 2 minutes.
          </motion.p>
          
          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-10"
          >
            <motion.button 
              onClick={() => user ? navigate('/submit') : login('/submit')}
              className="group inline-flex items-center gap-3 h-12 px-6 bg-white text-slate-900 font-medium rounded-full hover:bg-slate-100 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="cta-submit-btn"
            >
              <span>Submit your placement</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
          
          {/* Subtle trust line */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-8 text-sm text-slate-500"
          >
            100% anonymous · Verified data only
          </motion.p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8 bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <div className="font-heading text-sm font-semibold tracking-tight">
              Transplacement
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs sm:text-sm text-muted-foreground">
              <Link to="/about" className="hover:text-foreground transition-colors">About Us</Link>
              <span className="hidden sm:inline text-border">•</span>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
              <span className="hidden sm:inline text-border">•</span>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground">
              © 2025 Transplacement
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Action Button */}
      <FloatingActionButton />
    </div>
  );
};

export default Landing;
